import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, MessageCircle, Heart } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import { waLink } from "../../constants/data";
import { toast } from "sonner";

const TILE_COLORS = ["#FFE7F1", "#E7F4FF", "#FFF7DC", "#EAFBE7", "#F5E7FF"];

/**
 * ProductCard
 *  - Base card:   pass `product` only
 *  - Colour card: also pass `variant` — each variant renders as its own listing.
 */
export default function ProductCard({ product, variant }) {
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const navigate = useNavigate();

  // Resolve variant-specific fields with fallbacks to the base product
  const isVariant = !!variant;
  const displayImages = isVariant && (variant.images || []).length > 0 ? variant.images : product.images;
  const displayPrice = isVariant && (variant.price ?? null) !== null ? variant.price : product.price;
  const displayStock = isVariant ? variant.stock : product.stock;
  const outOfStock = displayStock <= 0;
  const colorName = isVariant ? variant.color_name : product.color_name;
  const colorHex = isVariant ? variant.color_hex : product.color_hex;
  // Variant short_description overrides product's short_description on the card
  const shortDesc = isVariant && (variant.short_description || "").trim()
    ? variant.short_description
    : product.short_description;
  // For wishlist / cart, key by product id + variant id
  const wishId = isVariant ? `${product.id}::${variant.id}` : product.id;
  const wishItem = isVariant
    ? { ...product, id: wishId, name: `${product.name}${colorName ? ` — ${colorName}` : ""}`, price: displayPrice, images: displayImages }
    : product;
  const wished = has(wishId);
  const bg = TILE_COLORS[Math.abs((product.slug || "").charCodeAt(0) + (variant?.id?.charCodeAt(0) || 0)) % TILE_COLORS.length];

  const productUrl = isVariant ? `/product/${product.slug}?color=${variant.id}` : `/product/${product.slug}`;

  const handleAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (outOfStock) return;
    if (isVariant) {
      // Provide the variant so cart tracks colour + variant stock server-side
      addItem({ ...product, price: displayPrice }, 1, "", {
        id: variant.id,
        color_name: variant.color_name,
        color_hex: variant.color_hex,
        images: variant.images,
        stock: variant.stock,
      });
    } else {
      addItem(product, 1);
    }
    toast.success(`${product.name}${colorName ? ` — ${colorName}` : ""} added to cart 🎁`);
  };

  const handleWish = (e) => {
    e.preventDefault(); e.stopPropagation();
    toggle(wishItem);
    toast.success(wished ? "Removed from wishlist" : "Saved with love ♥");
  };

  const displayName = isVariant && colorName ? `${product.name} — ${colorName}` : product.name;

  return (
    <div
      onClick={() => navigate(productUrl)}
      data-testid={isVariant ? `product-card-${product.slug}-${variant.id}` : `product-card-${product.slug}`}
      className="group doodle-card flex flex-col cursor-pointer"
      style={{ backgroundColor: bg }}
    >
      <div className="aspect-[3/4] overflow-hidden bg-white/40 relative" style={{ borderRadius: "20px 20px 0 0" }}>
        <img src={displayImages[0]} alt={displayName} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
        {outOfStock && (
          <div className="absolute top-3 left-3 doodle-pill bg-[#2D2424] text-white text-xs">Sold Out</div>
        )}
        {product.mrp && product.mrp > displayPrice && !outOfStock && (
          <div className="absolute top-3 right-3 doodle-pill bg-[#FFD93D] text-[#2D2424] text-xs">
            -{Math.round(((product.mrp - displayPrice) / product.mrp) * 100)}%
          </div>
        )}
        {colorName && (
          <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-white/95 border-2 border-[#2D2424] rounded-full pl-1 pr-2.5 py-1" data-testid={`color-badge-${wishId}`} style={{ boxShadow: "2px 2px 0 #2D2424" }}>
            <span className="w-4 h-4 rounded-full border border-[#2D2424]" style={{ backgroundColor: colorHex || "#FFE7F1" }} aria-hidden="true"/>
            <span className="text-[10px] font-hand text-[#2D2424] uppercase tracking-wider leading-none">{colorName}</span>
          </div>
        )}
        <button
          type="button"
          onClick={handleWish}
          data-testid={`wish-${wishId}`}
          className="absolute bottom-3 right-3 w-10 h-10 bg-white border-2 border-[#2D2424] rounded-full flex items-center justify-center"
          style={{ boxShadow: "2px 2px 0 #2D2424" }}
        >
          <Heart className="w-4 h-4" strokeWidth={2} fill={wished ? "#FF6B9D" : "none"} color="#FF6B9D"/>
        </button>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <Link to={productUrl} onClick={(e) => e.stopPropagation()} className="font-doodle text-2xl text-[#2D2424] hover:text-[#7A1F2B] leading-tight">
          {displayName}
        </Link>
        <p className="text-xs text-[#5C5555] mt-1 line-clamp-2 font-hand">{shortDesc}</p>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-baseline gap-2">
            <span className="font-doodle text-2xl text-[#7A1F2B]">₹{displayPrice.toLocaleString("en-IN")}</span>
            {product.mrp && product.mrp > displayPrice && (
              <span className="text-xs text-[#5C5555] line-through">₹{product.mrp.toLocaleString("en-IN")}</span>
            )}
          </div>
          <span className={`text-[10px] font-hand uppercase ${displayStock > 5 ? "text-green-700" : displayStock > 0 ? "text-amber-700" : "text-[#7A1F2B]"}`}>
            {outOfStock ? "Out" : displayStock > 5 ? "In stock" : `${displayStock} left`}
          </span>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={handleAdd}
            disabled={outOfStock}
            data-testid={`add-to-cart-${wishId}`}
            className="flex-1 btn-primary !py-2 !px-3 !text-xs"
          >
            <ShoppingBag className="w-3.5 h-3.5" strokeWidth={2}/> Add
          </button>
          <a
            href={waLink(`Hi! I'd like a bulk inquiry for ${displayName}.`)}
            target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`bulk-inquiry-${wishId}`}
            className="w-10 h-10 bg-white border-2 border-[#2D2424] flex items-center justify-center"
            style={{ borderRadius: 999, boxShadow: "2px 2px 0 #2D2424" }}
            title="Bulk Inquiry"
          >
            <MessageCircle className="w-4 h-4 text-[#25D366]" strokeWidth={2}/>
          </a>
        </div>
      </div>
    </div>
  );
}
